import { Application, Container } from "pixi.js";

import type { IStudioElement } from "../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Elements.interface";

import { EStudioElementType } from "../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Elements.interface";

import DrawCache from "./DrawCache";

import StrategyBounds from "./StrategyBounds";

import { TextElementRenderer } from "./Elements/TextElement";
import { cyberTextMotion, mountCyberTextMotion } from "@/src/entities/scenes/cyberSourcesRuntime";
import { UpdaterList } from "./Overlays/OverlayElementAnimator";

class ElementsFactory {
  public CyberAnim: { updaters: UpdaterList; startMs: number } | null = null;
  private DrawCache: DrawCache;

  private Root: Container;

  private textRenderer: TextElementRenderer;

  private Options = {
    width: 1920,

    height: 1080,
  };

  constructor(_app: Application, drawCache: DrawCache, root: Container) {
    this.DrawCache = drawCache;

    this.Root = root;

    this.textRenderer = new TextElementRenderer();
  }

  public async renderElements(elements: IStudioElement[]) {
    this.CyberAnim = null;
    const cyberUpdaters = new UpdaterList();
    const visibleKeys = new Set((elements ?? []).filter(element => element.active).map(element => `element_${element.id}`));
    for (const child of [...this.Root.children]) {
      if (child.label?.startsWith("element_el-sources-cyber-") && !visibleKeys.has(child.label)) {
        delete this.DrawCache.Containers[child.label];
        child.destroy({ children: true });
      }
    }
    if (!elements || elements.length === 0) {
      return;
    }

    for (const element of elements) {
      if (!element.active) continue;

      const elementKey = `element_${element.id}`;

      const sizeElement = StrategyBounds.getBounds({
        bound: element.bounds,

        container: {
          width: this.Options.width,

          height: this.Options.height,
        },
      });

      let elementContainer = this.DrawCache.Containers[elementKey];

      if (!elementContainer) {
        elementContainer = new Container();

        elementContainer.label = elementKey;

        this.DrawCache.Containers[elementKey] = elementContainer;

        this.Root.addChild(elementContainer);
      } else {
        if (cyberTextMotion(element)) elementContainer.destroy({ children: true });
        else elementContainer.removeChildren();

        elementContainer = new Container();

        elementContainer.label = elementKey;

        this.DrawCache.Containers[elementKey] = elementContainer;

        this.Root.addChild(elementContainer);
      }

      // Position container in absolute (world) coordinates

      elementContainer.width = sizeElement.width;

      elementContainer.height = sizeElement.height;

      elementContainer.position.set(sizeElement.left, sizeElement.top);

      elementContainer.zIndex = element.bounds.zIndex || 0;

      // Content is rendered in relative coordinates within the container

      const contentBounds = {
        x: 0,

        y: 0,

        width: sizeElement.width,

        height: sizeElement.height,
      };

      await this.renderElementContent(element, elementContainer, contentBounds);
      const cyberUpdate = mountCyberTextMotion(element, elementContainer);
      if (cyberUpdate) cyberUpdaters.push(cyberUpdate);

      this.Root.sortDirty = true;
    }
    if (cyberUpdaters.length) this.CyberAnim = { updaters: cyberUpdaters, startMs: Date.now() };
  }

  private async renderElementContent(
    element: IStudioElement,

    container: Container,

    destination: { x: number; y: number; width: number; height: number },
  ) {
    switch (element.type) {
      case EStudioElementType.TEXT:
        this.textRenderer.render(element, container, destination);

        break;
    }
  }

  public destroy() {
    this.CyberAnim = null;
    for (const key in this.DrawCache.Containers) {
      if (key.startsWith("element_")) {
        const container = this.DrawCache.Containers[key];

        if (container && !container.destroyed) {
          container.removeChildren();

          container.destroy();
        }

        delete this.DrawCache.Containers[key];
      }
    }
  }
}

export default ElementsFactory;
